<template>
	<div class="cog-choice">
		<c-select
			:id="id"
			ref="select"
			v-model="internalValue"
			:options="options"
			:readonly="readonly"
			:placeholder="placeholder"
			:allow-other="false"
			:filterable="true"
			filter-match="beginning"
			:chameleon="chameleon"
			:mobile="mobile"
			:scope="scope"
			@change="handleChange"
		/>
	</div>
</template>

<script>
	import CSelect from './Select.vue';
	import { getFormatPrecision } from 'src/util/helpers';
	const DEFAULT_MINIMUM = 1;
	const DEFAULT_MAXIMUM = 10;
	const DEFAULT_INCREMENT = 1;
	const OPTION_LIMIT = 1001;

	export default {
		name: 'CNumberDropdown',
		components: { CSelect },
		inject: ['$format', 'log'],
		inheritAttrs: false,
		model: {
			prop: 'value',
			event: 'change'
		},
		props: {
			id: { type: String, default: null },
			value: { type: String, default: null },
			max: { type: Number, default: DEFAULT_MAXIMUM },
			min: { type: Number, default: DEFAULT_MINIMUM },
			increment: { type: Number, default: DEFAULT_INCREMENT },
			format: { type: String, default: 'N0', required: true },
			scope: { type: String, default: '0' },
			placeholder: { type: String, default: null },
			readonly: { type: Boolean, default: false },
			chameleon: { type: Boolean, default: false },
			mobile: { type: Boolean, default: false }
		},
		data: function () {
			return {
				internalValue: this.value
			};
		},
		computed: {
			native() {
				return this.chameleon || this.mobile;
			},
			precision() {
				return getFormatPrecision(this.format);
			},
			cleanMin() {
				return this.min === null || isNaN(this.min) || this.min < Number.MIN_SAFE_INTEGER
					? (this.format.startsWith('P') ? 0 : DEFAULT_MINIMUM)
					: this.min;
			},
			cleanMax() {
				return this.max === null || isNaN(this.max) || this.max > Number.MAX_SAFE_INTEGER
					? (this.format.startsWith('P') ? 1 : DEFAULT_MAXIMUM)
					: this.max;
			},
			options: function () {
				let min = this.cleanMin;
				let max = this.cleanMax;
				if (min > max) {
					const temp = min;
					min = max;
					max = temp;
				}

				// The increment can never be zero
				const increment = Math.abs(this.increment || this.getDefaultIncrement(min, max));

				const options = [];
				let formattedValue = null;
				for (let i = min; i < max; i = this.toPrecision(i + increment)) {
					formattedValue = this.$format(i, this.format);
					if (options.length >= OPTION_LIMIT)
						break;
					else
						options.push({ displayValue: formattedValue, value: formattedValue });
				}

				// In order to always include an option for the max, add the last option outside the loop
				// to support situations where the max does not naturally fall on an increment.
				if (options.length < OPTION_LIMIT) {
					formattedValue = this.$format(max, this.format);
					options.push({ displayValue: formattedValue, value: formattedValue });
				}

				return options;
			}
		},
		watch: {
			value: function (valueProp) {
				this.internalValue = valueProp;
			},
			min: function(min) {
				if (this.value < min)
					this.$children[0].handleChange(null, this.value);
			},
			max: function(max) {
				if (this.value > max)
					this.$children[0].handleChange(null, this.value);
			}
		},
		methods: {
			toPrecision(num) {
				return parseFloat(Math.round(num * Math.pow(10, this.precision)) / Math.pow(10, this.precision));
			},
			getDefaultIncrement(min, max) {
				const increment = (max - min) / 10;
				return this.toPrecision(increment) || (this.format.startsWith('P') ? 0.05 : DEFAULT_INCREMENT);
			},
			handleChange(value, prevValue) {
				this.internalValue = value;
				this.$emit('change', value, prevValue);
			},
			handleBlur(ev) {
				this.$emit('blur', ev);
			}
		}
	};
</script>
